import { useEffect, useRef, useState } from "react";
import {
  animate,
  motion,
  MotionValue,
  useAnimation,
  useInView,
  useMotionValue,
  useTransform,
} from "framer-motion";

function ScrollFillText({
  progress,
  baseColor = "#A2A7B0",
  fillGradient,
  children,
  direction = "horizontal",
}: {
  progress: MotionValue<number>;
  baseColor?: string;
  fillGradient: string;
  children: React.ReactNode;
  direction?: "horizontal" | "vertical";
}) {
  const bgSize = useTransform(progress, (v) => {
    const pct = `${Math.round(v * 100)}%`;
    return direction === "vertical" ? `100% ${pct}` : `${pct} 100%`;
  });

  return (
    <span style={{ position: "relative", display: "inline-block" }}>
      <span style={{ color: baseColor }}>{children}</span>

      <motion.span
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          color: "transparent",
          backgroundImage: fillGradient,
          backgroundRepeat: "no-repeat",
          backgroundPosition: "0 0",
          backgroundSize: bgSize,
          WebkitBackgroundClip: "text",
          backgroundClip: "text",
          pointerEvents: "none",
          whiteSpace: "inherit",
        }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export const Section3 = () => {
  const ref = useRef<HTMLElement | null>(null);
  const inView = useInView(ref, { amount: 0.2, once: true });

  const rightCtrl = useAnimation();

  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 900);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  //  좌측 텍스트 채움 + 라인 진행률을 하나로 통일
  const fillProgress = useMotionValue(0);
  const linePct = useTransform(fillProgress, (v) => `${Math.round(v * 100)}%`);

  //  inView 순간 1회만 실행 보장
  const startedRef = useRef(false);

  useEffect(() => {
    if (!inView) return;
    if (startedRef.current) return;
    startedRef.current = true;

    // 시작 초기화
    fillProgress.set(0);

    // 오른쪽 초기 상태 (완전히 숨김)
    rightCtrl.set({
      opacity: 0,
      clipPath: "inset(0% 100% 0% 0%)",
      y: 8,
    });

    //  1) 라인 + 왼쪽 채움: 2초
    const controls = animate(fillProgress, 1, {
      duration: 2,
      ease: [0.22, 1, 0.36, 1],
    });

    //  2) 오른쪽: "2초 뒤" 바로 시작(텀 없음) + 서서히 등장
    rightCtrl.start({
      opacity: 1,
      clipPath: "inset(0% 0% 0% 0%)",
      y: 0,
      transition: {
        delay: 1.4, // 🔥 라인 끝나는 시점과 정확히 맞춤
        duration: 0.6, // 🔥 왼쪽처럼 서서히
        ease: [0.22, 1, 0.36, 1],
      },
    });

    return () => controls.stop();
  }, [inView, fillProgress, rightCtrl]);

  const baseFill = isMobile
    ? "linear-gradient(180deg, #000000 0%, #000000 100%)"
    : "linear-gradient(90deg, #000000 0%, #000000 100%)";

  const accentFill = isMobile
    ? "linear-gradient(180deg, #0079FF 0%, #0079FF 100%)"
    : "linear-gradient(90deg, #0079FF 0%, #0079FF 100%)";

  const textDirection = isMobile ? "vertical" : "horizontal";

  return (
    <section
      ref={ref}
      className="md:pt-[160px] pt-[80px] md:pb-[160px] pb-[80px] flex justify-center md:px-10"
    >
      <div
        className="w-full flex gap-10 md:gap-[36px] flex-col md:flex-row max-w-[1440px]"
        style={{ alignItems: isMobile ? "center" : "baseline" }}
      >
        {/* left: inView 순간부터 fillProgress로 채워짐 */}
        <div className="text-[36px] md:text-display1_3 font-bold leading-[1.15] md:leading-[1.1] text-center md:text-left">
          <ScrollFillText
            progress={fillProgress}
            baseColor="#A2A7B0"
            fillGradient={baseFill}
            direction={textDirection}
          >
            사용량 기반 <br /> 요금제 이기 때문에
          </ScrollFillText>
        </div>

        {/* line: fillProgress와 100% 동기화 */}
        <div className="flex flex-1">
          <div className="relative overflow-hidden rounded-full w-[4px] h-[120px] md:w-full md:h-[4px]">
            <div className="absolute inset-0 bg-[#D1D5DB]" />

            <motion.div
              className={isMobile ? "absolute left-0 top-0" : "absolute inset-y-0 left-0"}
              style={{
                width: isMobile ? "100%" : linePct,
                height: isMobile ? linePct : "100%",
                background: isMobile
                  ? "linear-gradient(180deg, rgba(0,104,255,0) 0%, #0068FF 100%)"
                  : "linear-gradient(90deg, rgba(0,104,255,0) 0%, #0068FF 100%)",
              }}
            />
          </div>
        </div>

        {/* right: 2초 끝난 즉시 표시 */}
        <motion.div
          className="w-fit flex flex-col items-center md:text-end font-bold md:text-display1_3 font-bold"
          initial={{ opacity: 0, clipPath: "inset(0% 100% 0% 0%)", y: 0 }}
          animate={rightCtrl}
        >
          {isMobile ? (
            <div className="flex flex-col items-center text-center">
              <div className="text-[36px] md:text-display1_3 font-bold leading-[1.15] md:leading-[1.1] text-center md:text-left">
                <ScrollFillText
                  progress={fillProgress}
                  baseColor="#A2A7B0"
                  fillGradient={accentFill}
                  direction={textDirection}
                >
                  비즈니스가
                </ScrollFillText>
              </div>

              <div className="text-[36px] md:text-display1_3 font-bold leading-[1.15] md:leading-[1.1] text-center md:text-left">
                <ScrollFillText
                  progress={fillProgress}
                  baseColor="#A2A7B0"
                  fillGradient={accentFill}
                  direction={textDirection}
                >
                  확장
                </ScrollFillText>
                <ScrollFillText
                  progress={fillProgress}
                  baseColor="#A2A7B0"
                  fillGradient={baseFill}
                  direction={textDirection}
                >
                  되어도
                </ScrollFillText>
              </div>

              <div className="text-[36px] md:text-display1_3 font-bold leading-[1.15] md:leading-[1.1] text-center md:text-left">
                <ScrollFillText
                  progress={fillProgress}
                  baseColor="#A2A7B0"
                  fillGradient={accentFill}
                  direction={textDirection}
                >
                  안정적이고 유연
                </ScrollFillText>
                <ScrollFillText
                  progress={fillProgress}
                  baseColor="#A2A7B0"
                  fillGradient={baseFill}
                  direction={textDirection}
                >
                  하게
                </ScrollFillText>
              </div>

              <div className="text-[36px] md:text-display1_3 font-bold leading-[1.15] md:leading-[1.1] text-center md:text-left">
                <ScrollFillText
                  progress={fillProgress}
                  baseColor="#A2A7B0"
                  fillGradient={baseFill}
                  direction={textDirection}
                >
                  확장 할 수 있어요
                </ScrollFillText>
              </div>
            </div>
          ) : (
            <ScrollFillText
              progress={fillProgress}
              baseColor="#A2A7B0"
              fillGradient={baseFill}
              direction={textDirection}
            >
              <ScrollFillText
                progress={fillProgress}
                baseColor="#A2A7B0"
                fillGradient={accentFill}
                direction={textDirection}
              >
                비즈니스가 확장
              </ScrollFillText>
              되어도 <br />
              <ScrollFillText
                progress={fillProgress}
                baseColor="#A2A7B0"
                fillGradient={accentFill}
                direction={textDirection}
              >
                안정적이고 유연
              </ScrollFillText>
              하게 <br />
              확장할 수 있어요
            </ScrollFillText>
          )}
        </motion.div>
      </div>
    </section>
  );
};
